/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import colors from '@sportninja/common/constants/appColors'
import type { DatePickerProps as AntDatePickerProps } from 'antd'
import { DatePicker as AntDatePicker } from 'antd'
import { CalendarOutlined } from '@ant-design/icons'
import Text from '../Text'
import { paragraphMedium, weightRegular } from 'src/components/css'
import dayjs from 'dayjs'

type DatePickerProps = {
  label: string
  value: AntDatePickerProps['value']
  onChange: AntDatePickerProps['onChange']
  minDate?: AntDatePickerProps['value']
  /** Tab index for keyboard navigation */
  tabIndex?: number
  /** Whether the date picker is disabled */
  disabled?: boolean
}

/**
 * Converts a date value to a dayjs object if it is a string.
 *
 * @param value - The date value to parse. Can be a string, dayjs object, or undefined.
 * @returns The value as a dayjs object if input is a string, otherwise returns the original value.
 */
const parseDateValueToDayjsIfNeeded = (value: any) => {
  if (typeof value === 'string') {
    return dayjs(value)
  }
  return value
}

const DatePicker = ({
  label,
  value,
  onChange,
  minDate,
  tabIndex,
  disabled,
}: DatePickerProps) => {
  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        gap: 8px;
        flex: 1 0 0;
        @media (max-width: 768px) {
          width: 100%;
        }
      `}
    >
      <Text variant='paragraphSmall' weight='regular' color={colors.WHITE}>
        {label}
      </Text>
      <AntDatePicker
        value={parseDateValueToDayjsIfNeeded(value)}
        onChange={onChange}
        disabledDate={
          minDate
            ? (current) => current && current.isBefore(minDate, 'day')
            : undefined
        }
        inputReadOnly
        tabIndex={tabIndex}
        disabled={disabled}
        css={css`
          height: 36px !important;
          ${paragraphMedium}
          ${weightRegular}
          width: 100%;
          border-radius: 8px;
          border: 1px solid ${colors.SECONDARY_100};
          background: ${colors.SECONDARY_200};
        `}
        getPopupContainer={(triggerNode) => {
          return triggerNode.parentNode as HTMLElement
        }}
        suffixIcon={
          <CalendarOutlined
            css={css`
              color: ${colors.WHITE};
              font-size: 20px;
            `}
          />
        }
        popupStyle={{ position: 'fixed' }}
      />
    </div>
  )
}

export default DatePicker
